import {
  IQuestion,
  IQuestionnaireData,
  TQuestionOption,
  TUtilsState,
  TGroup,
  ICompanyGroupsRRMHeaders,
  ICompanyGroupsRRM,
  IQuestionnaireCompaniesHeaders,
  IQuestionnaireCompanies,
  IQuestionnaireCompaniesTypes,
  IAppointmentsProgramsDataWithLabels,
  TAppointmentsProgramsTherapist,
  TAppointmentsProgramsUnavailableTherapist,
  TAppointmentsProgramsData,
  IAppointmentsProgramsMetaData,
  IAppointment,
  TGeneralRRM,
  TDataType,
  TAppointmentsProgramsScheduleResponse,
  TSmsMessage,
  TQuestionsUsersFields,
  TQuestionsSpecificTypes,
  TNamedItem,
  TSupportMessage,
  TGetStatusesResponse,
  TGetPendingAppointmentsProgramsResponse,
  TPlan,
  TCompanyService,
  TTreatmentRoom,
  IQuestionnaireCompaniesSpecificTypes,
} from "../types/utils.types";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { utilsApi } from "../api/utils.api";
import { tWithKeyAttr as t } from "../../i18nHelper";
import { sortOptionsByGroup } from "../../utils/utils";
import { TExercise, TExercisesCategory } from "../../utils/constants";
import { TCompany } from "../types/users.types";


const appointmentsProgramsDataInitialState: IAppointmentsProgramsDataWithLabels =
  {
    Medias: [],
    Roles: [],
    Periods: [],
    AppointmentsDurations: [],
    AppointmentsSequence: [],
    Genders: [],
    Migzars: [],
    TherapistsSpecializations: [],
    AppointmentsProgramsStatus: [],
    TreatmentTypes: [],
    FundingSources: [],
    TreatmentRoomsList: [],
    CompanyServicesList: [],
  };
const initialState: TUtilsState = {
  impersonationCompanyName: undefined,
  companies: [],
  superAdminCompaniesOptions: [{ value: -1, label: "הכל" }],
  questionnaires: [],
  questionnairesOptions: [],
  questionnaireCompaniesHeaders: [],
  questionnaireCompanies: [],
  questionnaireCompaniesTypes: [],
  questions: [],
  questionsSortedByQuestionnaire: {},
  questionOptions: [],
  questionOptionsGroupped: [],
  nextQuestionOptions: [],
  groups: [],
  companyGeneralRRM: [],
  companyGroupsRRMHeaders: [],
  companyGroupsRRM: [],
  features: [],
  exercises: [],
  exercisesCategories: [],
  exercisesCategoriesSelectOptions: [],
  error: "",
  appointmentsProgramsData: appointmentsProgramsDataInitialState,
  appointmentsProgramsTherapist: [],
  appointmentsProgramsUnavailableTherapists: [],
  userAppointmentsPrograms: [],
  companyAppointmentsPrograms: [],
  appointmentsProgramsSchedule: [],
  questionsAnswersDataTypesOptions: [],
  questionsSpecificTypes: [],
  smsMessages: [],
  supportMessages: [],
  supportStatuses: [],
  smsMessagesOptions: [],
  plans: [],
  plansOptions: [],
  questionsUsersFields: [],
  diagramsTypes: [],
  questionnaireCompaniesSpecificTypes: [],
  inquiryStatuses: [],
}

export const utilsSlice = createSlice({
  name: "utils",
  initialState,
  reducers: {
    resetQuestions: (state) => {
      return { ...state, questions: [] };
    },
    setImpersonationCompanyName: (
      state,
      { payload }: { payload: string | undefined }
    ) => {
      return { ...state, impersonationCompanyName: payload };
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        utilsApi.endpoints.getQuestionnaires.matchFulfilled,
        (state: TUtilsState, action: PayloadAction<IQuestionnaireData[]>) => {
          return {
            ...state,
            questionnaires: action.payload,
            questionnairesOptions: [
              { value: 0, label: t("WITHOUT_QUESTIONNAIRE"), id: 0 },
              ...action.payload.map((q) => ({
                value: q.id,
                id: q.id,
                label: `${q.desc}: ${q.statusId === 1 ? t('STATUS_ACTIVE') : t('STATUS_INACTIVE')}`,
              })),
            ],
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.addOrEditQuestionnaire.matchFulfilled,
        (state: TUtilsState, action: PayloadAction<IQuestionnaireData[]>) => {
          if (!Array.isArray(action.payload)) {
            return state; // Early return, no state update
          }
          return { ...state, questionnaires: action.payload };
        }
      )
      .addMatcher(
        utilsApi.endpoints.deleteQuestionnaire.matchFulfilled,
        (state: TUtilsState, action: PayloadAction<IQuestionnaireData[]>) => {
          return { ...state, questionnaires: action.payload };
        }
      )
      .addMatcher(
        utilsApi.endpoints.getExercises.matchFulfilled,
        (state: TUtilsState, action: PayloadAction<TExercise[]>) => {
          return {
            ...state,
            exercises: action.payload,
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.addOrEditExercises.matchFulfilled,
        (state: TUtilsState, action: PayloadAction<TExercise[]>) => {
          return {
            ...state,
            exercises: action.payload,
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.deleteExercises.matchFulfilled,
        (state: TUtilsState, action: PayloadAction<TExercise[]>) => {
          return {
            ...state,
            exercises: action.payload,
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.getExercisesCategories.matchFulfilled,
        (state: TUtilsState, action: PayloadAction<TExercisesCategory[]>) => {
          return {
            ...state,
            exercisesCategories: action.payload,
            exercisesCategoriesSelectOptions: action.payload.map((c) => ({
              value: c.name,
              id: c.id,
              label: c.name,
            })),
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.addOrEditExercisesCategories.matchFulfilled,
        (state: TUtilsState, action: PayloadAction<TExercisesCategory[]>) => {
          return {
            ...state,
            exercisesCategories: action.payload,
            exercisesCategoriesSelectOptions: action.payload.map((c) => ({
              value: c.name,
              id: c.id,
              label: c.name,
            })),
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.deleteExercisesCategories.matchFulfilled,
        (state: TUtilsState, action: PayloadAction<TExercisesCategory[]>) => {
          return {
            ...state,
            exercisesCategories: action.payload,
            exercisesCategoriesSelectOptions: action.payload.map((c) => ({
              value: c.name,
              id: c.id,
              label: c.name,
            })),
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.getQuestions.matchFulfilled,
        (state: TUtilsState, action: PayloadAction<IQuestion[]>) => {
          return {
            ...state,
            questions: action.payload.sort((a, b) => a.seq - b.seq),
            nextQuestionOptions: [
              ...action.payload
                .filter((q) => q.statusId === 1)
                .map((q) => ({
                  value: q.id.toString(),
                  label: q.desc,
                  id: q.id,
                })),
              {
                value: "null",
                label: t("NO_NEXT_QUESTION"),
                id: null,
              },
            ],
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.deleteQuestions.matchFulfilled,
        (state: TUtilsState, action: PayloadAction<IQuestion[]>) => {
          return {
            ...state,
            questions: action.payload.sort((a, b) => a.seq - b.seq),
            nextQuestionOptions: [
              ...action.payload
                .filter((q) => q.statusId === 1)
                .map((q) => ({
                  value: q.id.toString(),
                  label: q.desc,
                  id: q.id,
                })),
              {
                value: "null",
                label: t("NO_NEXT_QUESTION"),
                id: null,
              },
            ],
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.getQuestionsOptions.matchFulfilled,
        (state: TUtilsState, action: PayloadAction<TQuestionOption[]>) => {
          return {
            ...state,
            questionOptions: action.payload,
            questionOptionsGroupped: sortOptionsByGroup(action.payload),
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.addOrEditQuestionsOptions.matchFulfilled,
        (state: TUtilsState, action: PayloadAction<TQuestionOption[]>) => {
          return {
            ...state,
            questionOptions: action.payload,
            questionOptionsGroupped: sortOptionsByGroup(action.payload),
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.deleteQuestionsOptions.matchFulfilled,
        (state: TUtilsState, action: PayloadAction<TQuestionOption[]>) => {
          return {
            ...state,
            questionOptions: action.payload,
            questionOptionsGroupped: sortOptionsByGroup(action.payload),
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.getQuestionsGroups.matchFulfilled,
        (state: TUtilsState, action: PayloadAction<TGroup[]>) => {
          return {
            ...state,
            groups: action.payload,
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.addOrEditQuestionsGroups.matchFulfilled,
        (state: TUtilsState, action: PayloadAction<TGroup[]>) => {
          return {
            ...state,
            groups: action.payload,
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.deleteQuestionsGroups.matchFulfilled,
        (state: TUtilsState, action: PayloadAction<TGroup[]>) => {
          return {
            ...state,
            groups: action.payload,
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.getCompanyGroupsRRMHeaders.matchFulfilled,
        (
          state: TUtilsState,
          action: PayloadAction<ICompanyGroupsRRMHeaders[]>
        ) => {
          return {
            ...state,
            companyGroupsRRMHeaders: action.payload,
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.addOrEditCompanyGroupsRRMHeaders.matchFulfilled,
        (
          state: TUtilsState,
          action: PayloadAction<ICompanyGroupsRRMHeaders[]>
        ) => {
          if (Array.isArray(action.payload)) {
            return {
              ...state,
              companyGroupsRRMHeaders: action.payload,
            };
          }
        }
      )
      .addMatcher(
        utilsApi.endpoints.deleteCompanyGroupsRRMHeaders.matchFulfilled,
        (
          state: TUtilsState,
          action: PayloadAction<ICompanyGroupsRRMHeaders[]>
        ) => {
          return {
            ...state,
            companyGroupsRRMHeaders: action.payload,
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.getCompanyGroupsRRM.matchFulfilled,
        (state: TUtilsState, action: PayloadAction<ICompanyGroupsRRM[]>) => {
          return {
            ...state,
            companyGroupsRRM: action.payload.sort((a, b) => a.seq - b.seq),
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.addOrEditCompanyGroupsRRM.matchFulfilled,
        (state: TUtilsState, action: PayloadAction<ICompanyGroupsRRM[]>) => {
          return {
            ...state,
            companyGroupsRRM: action.payload.sort((a, b) => a.seq - b.seq),
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.getCompanyGeneralRRM.matchFulfilled,
        (state: TUtilsState, action: PayloadAction<TGeneralRRM[]>) => {
          return {
            ...state,
            companyGeneralRRM: action.payload.sort(
              (a, b) => a.rrmseq - b.rrmseq
            ),
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.addOrEditCompanyGeneralRRM.matchFulfilled,
        (state: TUtilsState, action: PayloadAction<TGeneralRRM[]>) => {
          return {
            ...state,
            companyGeneralRRM: action.payload.sort(
              (a, b) => a.rrmseq - b.rrmseq
            ),
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.deleteCompanyGeneralRRM.matchFulfilled,
        (state: TUtilsState, action: PayloadAction<TGeneralRRM[]>) => {
          return {
            ...state,
            companyGeneralRRM: action.payload.sort(
              (a, b) => a.rrmseq - b.rrmseq
            ),
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.getQuestionnaireCompaniesHeaders.matchFulfilled,
        (
          state: TUtilsState,
          action: PayloadAction<IQuestionnaireCompaniesHeaders[]>
        ) => {
          return {
            ...state,
            questionnaireCompaniesHeaders: action.payload,
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.addOrEditQuestionnaireCompaniesHeaders
          .matchFulfilled,
        (
          state: TUtilsState,
          action: PayloadAction<IQuestionnaireCompaniesHeaders[]>
        ) => {
          return {
            ...state,
            questionnaireCompaniesHeaders: action.payload,
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.deleteQuestionnaireCompaniesHeaders.matchFulfilled,
        (
          state: TUtilsState,
          action: PayloadAction<IQuestionnaireCompaniesHeaders[]>
        ) => {
          return {
            ...state,
            questionnaireCompaniesHeaders: action.payload,
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.getQuestionnaireCompanies.matchFulfilled,
        (
          state: TUtilsState,
          action: PayloadAction<IQuestionnaireCompanies[]>
        ) => {
          return {
            ...state,
            questionnaireCompanies: action.payload,
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.addOrEditQuestionnaireCompanies.matchFulfilled,
        (
          state: TUtilsState,
          action: PayloadAction<IQuestionnaireCompanies[]>
        ) => {
          return {
            ...state,
            questionnaireCompanies: action.payload,
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.deleteQuestionnaireCompanies.matchFulfilled,
        (
          state: TUtilsState,
          action: PayloadAction<IQuestionnaireCompanies[]>
        ) => {
          return {
            ...state,
            questionnaireCompanies: action.payload,
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.getQuestionnaireCompaniesTypesByCompanyId
          .matchFulfilled,
        (
          state: TUtilsState,
          action: PayloadAction<IQuestionnaireCompaniesTypes[]>
        ) => {
          return {
            ...state,
            questionnaireCompaniesTypes: action.payload.map((t) => ({
              ...t,
              value: t.id,
              label: t.name,
            })),
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.postQuestionnaireCompaniesTypes.matchFulfilled,
        (
          state: TUtilsState,
          action: PayloadAction<IQuestionnaireCompaniesTypes[]>
        ) => {
          // @ts-ignore
          if(action.payload?.success==="false"){
            return ;
          }
          return {
            ...state,
            questionnaireCompaniesTypes: action.payload.map((t) => ({
              ...t,
              value: t.id,
              label: t.name,
            })),
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.getAppointmentsProgramsTherapists.matchFulfilled,
        (
          state: TUtilsState,
          action: PayloadAction<TAppointmentsProgramsTherapist[]>
        ) => {
          return {
            ...state,
            appointmentsProgramsTherapist: action.payload.map((t) => ({
              ...t,
              value: t.therapistsId,
              label: t.fullName,
            })),
          };
        }
      )
     .addMatcher(
        utilsApi.endpoints.getAppointmentsProgramsUnavailableTherapists.matchFulfilled,
        (
          state: TUtilsState,
          action: PayloadAction<TAppointmentsProgramsUnavailableTherapist[]>
        ) => {
          return {
            ...state,
            appointmentsProgramsUnavailableTherapists: action.payload.map((t) => ({
              ...t,
              value: t.therapistsId,
              label: t.fullName,
            })),
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.getAppointmentsProgramsByUserId.matchFulfilled,
        (
          state: TUtilsState,
          action: PayloadAction<TAppointmentsProgramsData[]>
        ) => {
          return {
            ...state,
            userAppointmentsPrograms: Array.isArray(action.payload) ? action.payload : [],
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.getAppointmentsProgramsByCompanyId.matchFulfilled,
        (
          state: TUtilsState,
          action: PayloadAction<TGetPendingAppointmentsProgramsResponse>
        ) => {
          // Flatten all arrays into a single array of appointmentsPrograms for backward compatibility
          const allPrograms = [
            ...(action.payload.pendingForApproval || []),
            ...(action.payload.pendingForTherapist || []),
            ...(action.payload.assigned || []),
          ].map((item) => item.appointmentsProgram);
          return {
            ...state,
            companyAppointmentsPrograms: allPrograms,
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.getAppointmentsProgramsByTherapistsId.matchFulfilled,
        (
          state: TUtilsState,
          action: PayloadAction<TAppointmentsProgramsData[]>
        ) => {
          return {
            ...state,
            userAppointmentsPrograms: Array.isArray(action.payload) 
              ? action.payload.filter(
                  (ap) =>
                    ap.therapistsGuid != null &&
                    ap.appointmentsProgram.statusId !== 0
                )
              : [],
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.getAppointmentsProgramsByTherapistsId.matchPending,
        (state: TUtilsState) => {
          return {
            ...state,
            userAppointmentsPrograms: [],
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.getCompanyAppointmentsPrograms.matchFulfilled,
        (
          state: TUtilsState,
          action: PayloadAction<TAppointmentsProgramsData[]>
        ) => {
          return {
            ...state,
            companyAppointmentsPrograms: action.payload.map((a) => {
              const { appointmentsProgram, protocols } = a;
              const {
                name,
                appointmentsDurationsId,
                appointmentsDurationsOpenMinutes,
                appointmentsSequenceId,
                appointmentsSequenceOpenCountInPeriod,
                countOfAppointments,
                genderId,
                roleId,
                therapistsSpecializationsIds,
                comments,
                mediasId,
                migzarId,
                vetek,
                protocolId,
                treatmentRoomId,
                fundingSourcesId,
                treatmentTypesId,
              } = appointmentsProgram;
              return {
                name,
                appointmentsDurationsId,
                appointmentsDurationsOpenMinutes,
                appointmentsSequenceId,
                appointmentsSequenceOpenCountInPeriod,
                countOfAppointments,
                genderId,
                roleId,
                therapistsSpecializationsIds,
                comments,
                mediasId,
                migzarId,
                vetek,
                protocolId,
                treatmentRoomId,
                fundingSourcesId,
                treatmentTypesId,
                protocols,
              };
            }),
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.postAppointmentsPrograms.matchFulfilled,
        (
          state: TUtilsState,
          action: PayloadAction<TAppointmentsProgramsData[]>
        ) => {
          return {
            ...state,
            userAppointmentsPrograms: Array.isArray(action.payload) ? action.payload : [],
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.getAppointmentsProgramsSchedule.matchFulfilled,
        (
          state: TUtilsState,
          action: PayloadAction<TAppointmentsProgramsScheduleResponse[]>
        ) => {
          return {
            ...state,
            appointmentsProgramsSchedule: action.payload.map(
              (a) => a.appointments
            ),
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.deleteAppointmentsProgramWithAllAppointments
          .matchFulfilled,
        (state: TUtilsState, action: PayloadAction<IAppointment[]>) => {
          return {
            ...state,
            appointmentsProgramsSchedule: action.payload.sort(
              (a, b) => a.id - b.id
            ),
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.deleteAppointment.matchFulfilled,
        (state: TUtilsState, action: PayloadAction<IAppointment[]>) => {
          return {
            ...state,
            appointmentsProgramsSchedule: action.payload.sort(
              (a, b) => a.id - b.id
            ),
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.getAllOptionsDataTypes.matchFulfilled,
        (state: TUtilsState, action: PayloadAction<TDataType[]>) => {
          return {
            ...state,
            questionsAnswersDataTypesOptions:
              action.payload?.map((t) => ({
                ...t,
                value: t.id.toString(),
                label: t.name || t.desc,
              })) || [],
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.getAllQuestionsSpecificTypes.matchFulfilled,
        (
          state: TUtilsState,
          action: PayloadAction<TQuestionsSpecificTypes[]>
        ) => {
          return {
            ...state,
            questionsSpecificTypes:
              action.payload
                ?.filter((t) => t.statusId)
                .map((t) => ({
                  ...t,
                  value: t.id.toString(),
                  label: t.name || t.nameEN,
                })) || [],
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.getAllQuestionsUsersFields.matchFulfilled,
        (
          state: TUtilsState,
          action: PayloadAction<TQuestionsUsersFields[]>
        ) => {
          const temp =
            action.payload?.map((t) => ({
              ...t,
              value: t.usersField,
              label: t.name ?? t.usersField,
            })) || [];
          // if (temp.length) {
          //   temp.push({ id: -1, value: "null", label: "ללא", usersField: "ללא" })
          // }

          return {
            ...state,
            questionsUsersFields: temp,
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.getAllMetaTables.matchFulfilled,
        (
          state: TUtilsState,
          action: PayloadAction<IAppointmentsProgramsMetaData>
        ) => {
          const appointmentsSequenceWithLabels =
            action.payload.AppointmentsSequence.map((sequence) => {
              const period = action.payload.Periods.find(
                (p) => p.Id === sequence.PeriodsId
              );
              let label = `${sequence.CountInPeriod} ${
                period ? period.Name.toLowerCase() : "Unknown"
              }`;
              label = label.replace("1", t('ONCE')).replace("2", t('TWICE'));
              return {
                ...sequence,
                value: sequence.Id,
                label,
              };
            });
          return {
            ...state,
            appointmentsProgramsData: {
              ...state.appointmentsProgramsData,
              AppointmentsDurations:
                action.payload?.AppointmentsDurations.filter(
                  (d) => d.Minutes !== null
                ).map((d) => ({
                  ...d,
                  value: d.Id,
                  label: d.Minutes || "No duration",
                })),
              AppointmentsSequence: appointmentsSequenceWithLabels,
              Medias: action.payload?.Medias.map((m) => ({
                ...m,
                value: m.Id,
                label: m.Name || "",
              })),
              Roles: action.payload?.Roles.map((r) => ({
                ...r,
                value: r.Id,
                label: r.Name,
              })).concat([
                { Id: 0, value: 0, Name: t("UNAFFILIATED"), label: t("UNAFFILIATED") },
              ]),
              Genders: action.payload?.Genders.map((g) => ({
                ...g,
                value: g.Id,
                label: g.Name,
              })).concat([
                  { Id: 0, value: 0, Name: t("UNAFFILIATED"), label: t("UNAFFILIATED") },
              ]),
              Migzars: action.payload?.Migzars.map((m) => ({
                ...m,
                value: m.Id,
                label: m.Name,
              })).concat([
                { Id: 0, value: 0, Name: t("UNAFFILIATED"), label: t("UNAFFILIATED") },
              ]),
              TherapistsSpecializations: action.payload.TherapistsSpecializations?.map((s) => ({
                ...s,
                value: s.Id,
                label: s.Name,
              })).concat([
                { Id: 0, value: 0, Name: t("UNAFFILIATED"), label: t("UNAFFILIATED") },
              ]) || [],
              AppointmentsProgramsStatus:
                action.payload.AppointmentsProgramsStatus?.map((s) => ({
                  ...s,
                  value: s.Id,
                  label: s.Desc,
                })).concat([{ Id: -1, value: -1, label: "הכל", Desc: "הכל" }]),
              // do the options of treatmentTypeId in locales mode.
              TreatmentTypes: action.payload.TreatmentTypes?.map((t) => ({
                ...t,
                value: t.Id,
                label: t.Name,
              })) || [],
              FundingSources: action.payload.FundingSources?.map((f) => ({
                ...f,
                value: f.Id,
                label: f.Name,
              })) || [],
              TreatmentRoomsList: action.payload?.TreatmentRoomsList?.map((r: TTreatmentRoom) => ({
                ...r,
                value: r.Id,
                label: `${r.Name}${r.Capacity ? ` (${r.Capacity})` : ''}${r.Location ? ` - ${r.Location}` : ''}`,
              })) || [],
              CompanyServicesList: action.payload?.CompanyServicesList?.map((s: TCompanyService) => ({
                ...s,
                value: s.Id,
                label: s.Name,
              })) || [],
            },
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.getAllSmsMessages.matchFulfilled,
        (state: TUtilsState, action: PayloadAction<TSmsMessage[]>) => {
          return {
            ...state,
            smsMessages: action.payload,
            smsMessagesOptions: action.payload?.map((m) => ({
              value: m.id,
              label: m.title,
              // id: m.id,
            })),
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.postSmsMessage.matchFulfilled,
        (state: TUtilsState, action: PayloadAction<TSmsMessage[]>) => {
          return {
            ...state,
            smsMessages: action.payload,
          };
        }
      )

      .addMatcher(
        utilsApi.endpoints.postPlan.matchFulfilled,
        (state: TUtilsState, action: PayloadAction<TPlan[]>) => {
          return {
            ...state,
            plans: action.payload,
          };
        }
      )

      .addMatcher(
        utilsApi.endpoints.getAllPlans.matchFulfilled,
        (state: TUtilsState, action: PayloadAction<TPlan[]>) => {
          return {
            ...state,
            plans: action.payload,
            plansOptions: action.payload?.map((m) => ({
              value: m.id,
              label: m.name,
            })),
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.deletePlan.matchFulfilled,
        (state: TUtilsState, action: PayloadAction<TPlan[]>) => {
          return {
            ...state,
            plans: action.payload,
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.getCompanies.matchFulfilled,
        (state: TUtilsState, action: PayloadAction<TCompany[]>) => {
          return {
            ...state,
            companies: action.payload,
            superAdminCompaniesOptions: action.payload?.map((c) => ({
              ...c,
              label: c.name,
              value: c.id,
            })),
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.addOrEditCompany.matchFulfilled,
        (state: TUtilsState, action: PayloadAction<TCompany[]>) => {
          return {
            ...state,
            companies: action.payload,
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.deleteCompany.matchFulfilled,
        (state: TUtilsState, action: PayloadAction<TCompany[]>) => {
          return {
            ...state,
            companies: action.payload,
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.restoreCompany.matchFulfilled,
        (state: TUtilsState, action: PayloadAction<TCompany[]>) => {
          return {
            ...state,
            companies: action.payload,
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.deleteSmsMessage.matchFulfilled,
        (state: TUtilsState, action: PayloadAction<TSmsMessage[]>) => {
          return {
            ...state,
            smsMessages: action.payload,
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.getSupportMessages.matchFulfilled,
        (state: TUtilsState, action: PayloadAction<TSupportMessage[]>) => {
          if (Array.isArray(action.payload)) {
            return {
              ...state,
              supportMessages: action.payload,
            };
          }
        }
      )
      .addMatcher(
        utilsApi.endpoints.getSupportStatuses.matchFulfilled,
        (state: TUtilsState, action: PayloadAction<TGetStatusesResponse>) => {
          if (Array.isArray(action.payload)) {
            return {
              ...state,
              supportStatuses: action.payload,
            };
          }
        }
      )
      .addMatcher(
        utilsApi.endpoints.getQuestionnaireCompaniesSpecificTypes.matchFulfilled,
        (state: TUtilsState, action: PayloadAction<IQuestionnaireCompaniesSpecificTypes[]>) => {
          return {
            ...state,
            questionnaireCompaniesSpecificTypes: action.payload.map((t) => ({
              value: t.id,
              label: t.name,
            })).concat([
              { value: -1, label: t("UNAFFILIATED") },
            ]),
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.getDiagramsTypes.matchFulfilled,
        (state: TUtilsState, action: PayloadAction<TNamedItem[]>) => {
          return {
            ...state,
            diagramsTypes: action.payload.map((item) => ({
              value: item.Id.toString(),
              label: item.Name,
            })),
          };
        }
      )
      .addMatcher(
        utilsApi.endpoints.getStatuses.matchFulfilled,
        (state: TUtilsState, action: PayloadAction<TGetStatusesResponse>) => {
          return {
            ...state,
            inquiryStatuses: action.payload,
          };
        }
      );
  },
});

export const { actions, reducer } = utilsSlice;
export default utilsSlice.reducer;
